// VirtualBrowserPanel: a VNC-like remote-controlled browser surface.
// Renders JPEG frames streamed over the WebSocket and relays pointer /
// keyboard / scroll events back.

import { useEffect, useRef, useState, useCallback } from "react";
import type { RoomActions, RoomState } from "../socket/useRoomSocket.js";

export interface VirtualBrowserPanelProps {
  state: RoomState;
  actions: RoomActions;
  onScreenFrame: (cb: (data: ArrayBuffer) => void) => () => void;
  onClose: () => void;
}

export function VirtualBrowserPanel({
  state,
  actions,
  onScreenFrame,
  onClose
}: VirtualBrowserPanelProps): JSX.Element | null {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [address, setAddress] = useState("");

  // Render the JPEG stream into <img> by replacing the object URL.
  useEffect(() => {
    let lastUrl: string | null = null;
    const unsub = onScreenFrame((data) => {
      const blob = new Blob([data], { type: "image/jpeg" });
      const url = URL.createObjectURL(blob);
      if (imgRef.current) imgRef.current.src = url;
      if (lastUrl) URL.revokeObjectURL(lastUrl);
      lastUrl = url;
    });
    return () => {
      unsub();
      if (lastUrl) URL.revokeObjectURL(lastUrl);
    };
  }, [onScreenFrame]);

  const status = state.browser.status;
  const isOpen = status === "open" || status === "opening";
  console.log("[vb-panel] render: streams=", state.detectedStreams.length, "status=", status);

  // Coords are normalized 0..1. We compute them relative to the IMG
  // (not the .vb-screen container) because the JPEG is letterboxed — the
  // image is smaller than the container when their aspect ratios differ.
  // Mapping a click in the black bars to the viewport would offset the
  // remote cursor; mapping from the image keeps it accurate.
  const computeNormalized = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const img = imgRef.current;
      if (!img) return null;
      const rect = img.getBoundingClientRect();
      const w = rect.width || 1;
      const h = rect.height || 1;
      const x = Math.min(1, Math.max(0, (clientX - rect.left) / w));
      const y = Math.min(1, Math.max(0, (clientY - rect.top) / h));
      return { x, y };
    },
    []
  );

  const dispatchMouse = useCallback(
    (
      event: "mouseMoved" | "mousePressed" | "mouseReleased",
      native: React.MouseEvent<HTMLDivElement>
    ) => {
      const c = computeNormalized(native.clientX, native.clientY);
      if (!c) return;
      actions.browserMouse(event, c.x, c.y, {
        button:
          native.button === 2 ? "right" : native.button === 1 ? "middle" : "left",
        buttons: native.buttons,
        clickCount: native.detail,
        modifiers: mods(native)
      });
    },
    [actions, computeNormalized]
  );

  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      const c = computeNormalized(e.clientX, e.clientY);
      if (!c) return;
      actions.browserWheel(c.x, c.y, e.deltaX, e.deltaY);
    },
    [actions, computeNormalized]
  );

  const onKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isOpen) return;
      e.preventDefault();
      const event = e.type === "keydown" ? "keyDown" : "keyUp";
      actions.browserKey(event, e.key, e.code, {
        text: event === "keyDown" && e.key.length === 1 ? e.key : undefined,
        modifiers: keyMods(e),
        autoRepeat: e.repeat
      });
    },
    [actions, isOpen]
  );

  const navigate = useCallback(() => {
    const url = address.trim();
    if (!url) return;
    actions.browserNavigate(url);
  }, [address, actions]);

  if (!isOpen) return null;

  return (
    <div className="vb-overlay">
      <div className="vb-toolbar">
        <button onClick={() => actions.browserAction("back")}>←</button>
        <button onClick={() => actions.browserAction("forward")}>→</button>
        <button onClick={() => actions.browserAction("reload")}>↻</button>
        <input
          type="url"
          placeholder="https://example.com"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && navigate()}
        />
        <button onClick={navigate}>Go</button>
        <span className="status">{state.browser.activeUrl ?? ""}</span>
        <button className="close" onClick={onClose}>×</button>
      </div>
      <div
        ref={containerRef}
        className="vb-screen"
        tabIndex={0}
        onMouseMove={(e) => dispatchMouse("mouseMoved", e)}
        onMouseDown={(e) => dispatchMouse("mousePressed", e)}
        onMouseUp={(e) => dispatchMouse("mouseReleased", e)}
        onWheel={onWheel}
        onKeyDown={onKey}
        onKeyUp={onKey}
        autoFocus
      >
        <img ref={imgRef} alt="Remote browser" />
        {status !== "open" && <div className="vb-status">{status}…</div>}
      </div>
      {state.detectedStreams.length > 0 && (
        <div className="vb-streams">
          <h4>Detected streams</h4>
          <ul>
            {state.detectedStreams.map((s) => (
              <li key={s.id} className="vb-stream-card">
                <div className="meta">
                  <div className="kind">{s.kind}</div>
                  <div className="url" title={s.displayUrl}>{s.displayUrl}</div>
                  {s.resolutions.length > 0 && (
                    <div className="resolutions">
                      {s.resolutions.map((r, i) => (
                        <span key={i} className="badge">{r.width}×{r.height}{r.bandwidth ? ` · ${Math.round(r.bandwidth / 1000)}kbps` : ""}</span>
                      ))}
                    </div>
                  )}
                  <div className="stats">
                    {s.duration !== null && <span>Duration: {fmtDuration(s.duration)}</span>}
                    {s.liveWindowDuration !== null && <span>Live window: {s.liveWindowDuration.toFixed(1)}s</span>}
                    {s.byteLength !== null && <span>{s.byteLength} bytes</span>}
                    {s.mimeType && <span>{s.mimeType}</span>}
                  </div>
                </div>
                <button
                  onClick={() =>
                    actions.loadSource({
                      kind: "browserCandidate",
                      browserSessionId: state.browser.sessionId ?? "",
                      candidateId: s.id
                    })
                  }
                >
                  Use stream
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="vb-hint">
        Click the screen to capture keyboard. All participants can see and control this browser.
      </div>
    </div>
  );
}

function fmtDuration(sec: number): string {
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function mods(e: React.MouseEvent | React.WheelEvent | React.KeyboardEvent): number {
  let m = 0;
  if (e.altKey) m |= 1;
  if (e.ctrlKey) m |= 2;
  if (e.metaKey) m |= 4;
  if (e.shiftKey) m |= 8;
  return m;
}

function keyMods(e: React.KeyboardEvent): number {
  return mods(e);
}